import React, { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { getMe } from 'api';
import { clearToken } from 'utils/auth';
import ProfileModal from 'components/ProfileModal';
import { useWikiPages, WikiSidebar } from 'features/wiki';

const GlobalLayout = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const menuRef = useRef(null);
  const {
    pages,
    loading: pagesLoading,
    error: pagesError,
    refresh: refreshWikiPages,
    reorderPages,
  } = useWikiPages();

  useEffect(() => {
    let active = true;
    getMe()
      .then((data) => {
        if (active) setMe(data);
      })
      .catch(() => {
        // On 401, the client interceptor redirects to the login page.
      });
    return () => {
      active = false;
    };
  }, []);

  // Close the menu when clicking outside of it.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleLogout = () => {
    setOpen(false);
    clearToken();
    navigate('/login', { replace: true });
  };

  const handleOpenProfile = () => {
    setOpen(false);
    setShowProfile(true);
  };

  const handleProfileUpdated = (updated) => {
    setMe((prev) => ({ ...prev, displayName: updated.username, email: updated.email }));
  };

  return (
    <div className="min-vh-100">
      <header className="gh-header">
        <div className="container d-flex align-items-center justify-content-between">
          <Link to="/" className="gh-header-title">
            <i className="bi bi-journal-text" aria-hidden="true"></i>
            Kira Wiki
          </Link>
          <div className="position-relative" ref={menuRef}>
            <button
              type="button"
              className="btn btn-sm text-white d-inline-flex align-items-center gap-2"
              style={{ boxShadow: 'none' }}
              onClick={() => setOpen((prev) => !prev)}
              aria-expanded={open}
            >
              <i className="bi bi-person-circle" aria-hidden="true"></i>
              {me && me.displayName}
              <i className="bi bi-caret-down-fill small" aria-hidden="true"></i>
            </button>
            <ul
              className={`dropdown-menu dropdown-menu-end mt-1${open ? ' show' : ''}`}
              style={{ top: '100%', right: 0 }}
            >
              <li>
                <button type="button" className="dropdown-item d-flex align-items-center" onClick={handleOpenProfile}>
                  <i className="bi bi-person-gear me-2" aria-hidden="true"></i>
                  Profile
                </button>
              </li>
              <li><hr className="dropdown-divider" /></li>
              <li>
                <button type="button" className="dropdown-item d-flex align-items-center" onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right me-2" aria-hidden="true"></i>
                  Logout
                </button>
              </li>
            </ul>
          </div>
        </div>
      </header>
      <div className="container py-4">
        <div className="wiki-layout">
          <WikiSidebar pages={pages} loading={pagesLoading} error={pagesError} onReorder={reorderPages} />
          <main className="wiki-main">
            <Outlet context={{ me, pages, refreshWikiPages }} />
          </main>
        </div>
      </div>

      {showProfile && (
        <ProfileModal
          me={me}
          onClose={() => setShowProfile(false)}
          onUpdated={handleProfileUpdated}
        />
      )}
    </div>
  );
};

export default GlobalLayout;
